import "@silevis/reactgrid/styles.css";

import { ReactElement, useEffect, useMemo, useState } from "react";
import { CellChange, Column, Id, MenuOption, ReactGrid } from "@silevis/reactgrid";
import { Flex } from "@mantine/core";
import { mapColumnsToSpreadsheetColumns, resizeColumn } from "./utils/columns";
import { applyChangesToRows, mapRowsToSpreadsheetRows } from "./utils/rows";
import { SpreadsheetRowData } from "./types";
import { getContextMenuOptions } from "./utils/context-menu";

// TODO: KNOWN ISSUES WITH THE COMPONENT
// - If you delete a row and then undo, the row is added back, but not at its initial position. It is added at the end of the table.

type SpreadsheetProps<T extends SpreadsheetRowData> = {
    columns: Array<Extract<keyof T, string>>;
    rows: Array<T>;
    onRowsChange: (newRows: Array<T>) => void;
    dataId: string;
    editable?: boolean;
};

export const Spreadsheet = <T extends SpreadsheetRowData>({
    columns,
    rows,
    onRowsChange,
    dataId,
    editable = false,
}: SpreadsheetProps<T>): ReactElement => {
    const [spreadsheetColumns, setSpreadsheetColumns] = useState<Array<Column>>(() =>
        mapColumnsToSpreadsheetColumns(columns),
    );
    const spreadsheetRows = useMemo(
        () =>
            mapRowsToSpreadsheetRows({
                columns,
                rows,
                editable,
            }),
        [columns, rows, editable],
    );

    // ----- START ----- COLUMN RESIZING ----- START -----
    const handleColumnResize = (columnId: Id, width: number): void => {
        setSpreadsheetColumns((prevColumns) =>
            resizeColumn({
                columns: prevColumns,
                columnId,
                width,
            }),
        );
    };
    // ----- END ----- COLUMN RESIZING ----- END -----

    // ----- START ----- CELL CHANGES HANDLING ----- START -----
    const [currentChangesHistoryIndex, setCurrentChangesHistoryIndex] = useState<number>(-1);
    const [changesHistory, setChangesHistory] = useState<
        Array<{
            cellChanges: Array<CellChange>;
            deletedRows: Array<T>;
            addedRows: Array<T>;
        }>
    >([]);

    const onCellsChange = ({
        cellChanges,
        deletedRows,
        addedRows,
        addToHistory,
    }: {
        cellChanges: Array<CellChange>;
        deletedRows: Array<T>;
        addedRows: Array<T>;
        addToHistory: boolean;
    }): void => {
        if (addToHistory) {
            setChangesHistory((changesHistory) => [
                ...changesHistory.slice(0, currentChangesHistoryIndex + 1),
                {
                    cellChanges,
                    deletedRows,
                    addedRows,
                },
            ]);
            setCurrentChangesHistoryIndex(currentChangesHistoryIndex + 1);
        }

        const newRows: Array<T> = applyChangesToRows({
            changes: cellChanges,
            rows,
        });

        const filteredRows: Array<T> = newRows.filter(
            ({ rowId }) => !deletedRows.some((deletedRow) => deletedRow.rowId === rowId),
        );

        const finalRows: Array<T> = [...filteredRows, ...addedRows];

        onRowsChange(finalRows);
    };

    const onRowsDelete = (rowIds: Array<Id>): void => {
        const deletedRows: Array<T> = rows.filter((row) => rowIds.includes(row.rowId));

        onCellsChange({
            cellChanges: [],
            addedRows: [],
            deletedRows: deletedRows,
            addToHistory: true,
        });
    };

    const onRowAdd = (): void => {
        // Create a new row with empty cells for each column, and a unique rowId
        const newRow: T = {
            rowId: new Date().toISOString(),
            ...Object.fromEntries(columns.map((col) => [col, ""])),
        } as T;

        onCellsChange({
            cellChanges: [],
            addedRows: [newRow],
            deletedRows: [],
            addToHistory: true,
        });
    };
    // ----- END ----- CELL CHANGES HANDLING ----- END -----

    // ----- START ----- UNDO/REDO HANDLING ----- START -----
    useEffect(() => {
        if (!editable) {
            return;
        }

        const handleUndoChanges = (): void => {
            const indexToAccess = currentChangesHistoryIndex;

            if (indexToAccess >= 0 && changesHistory[indexToAccess]) {
                const mappedCellChanges: Array<CellChange> = changesHistory[
                    indexToAccess
                ].cellChanges.map(
                    ({ previousCell, newCell, ...rest }): CellChange =>
                        ({
                            // Revert the change by swapping old and new values
                            newCell: previousCell,
                            previousCell: newCell,
                            ...rest,
                        }) as CellChange,
                );

                onCellsChange({
                    cellChanges: mappedCellChanges,
                    // Deleted rows become added rows and vice versa because we are undoing the changes
                    addedRows: changesHistory[indexToAccess].deletedRows,
                    deletedRows: changesHistory[indexToAccess].addedRows,
                    addToHistory: false,
                });
                setCurrentChangesHistoryIndex(indexToAccess - 1);
            }
        };

        const handleRedoChanges = (): void => {
            const indexToAccess = currentChangesHistoryIndex + 1;

            if (indexToAccess < changesHistory.length && changesHistory[indexToAccess]) {
                onCellsChange({
                    cellChanges: changesHistory[indexToAccess].cellChanges,
                    deletedRows: changesHistory[indexToAccess].deletedRows,
                    addedRows: changesHistory[indexToAccess].addedRows,
                    addToHistory: false,
                });
                setCurrentChangesHistoryIndex(indexToAccess);
            }
        };

        const onKeyDown = (e: KeyboardEvent): void => {
            const key = e.key.toLowerCase();

            if (e.ctrlKey || e.metaKey) {
                // 'CTRL + Z' for undo
                if (!e.shiftKey && key === "z") {
                    handleUndoChanges();
                    e.preventDefault();
                    return;
                }

                // 'CTRL + Y' or 'CTRL + SHIFT + Z' for redo
                if ((e.shiftKey && key === "z") || key === "y") {
                    handleRedoChanges();
                    e.preventDefault();
                    return;
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return (): void => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [editable, currentChangesHistoryIndex, changesHistory, onCellsChange]);
    // ----- END ----- UNDO/REDO HANDLING ----- END -----

    return (
        <Flex
            sx={{
                overflowX: "auto",
            }}
            pb="24px"
            data-id={dataId}
        >
            <ReactGrid
                // Column definitions
                columns={spreadsheetColumns}
                // Actual data (with sticky header)
                rows={spreadsheetRows}
                stickyTopRows={1}
                // Row editing
                onCellsChanged={
                    editable
                        ? (changes): void => {
                              onCellsChange({
                                  cellChanges: changes,
                                  deletedRows: [],
                                  addedRows: [],
                                  addToHistory: true,
                              });
                          }
                        : undefined
                }
                enableFillHandle={editable}
                enableRangeSelection={editable}
                // Column resizing
                enableColumnResizeOnAllHeaders
                onColumnResized={handleColumnResize}
                // Delete rows
                onContextMenu={(
                    selectedRowIds,
                    selectedColIds,
                    selectionMode,
                    defaultMenuOptions,
                    selectedRanges,
                ): Array<MenuOption> => {
                    if (!editable) {
                        return [];
                    }

                    return getContextMenuOptions({
                        selectedRowIds,
                        selectedColIds,
                        selectionMode,
                        defaultMenuOptions,
                        selectedRanges,
                        onRowsDelete,
                        onRowAdd,
                    });
                }}
            />
        </Flex>
    );
};
